import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { isAxiosError } from 'axios';

import type {
  InviteEligibilityApplicant,
  InviteEligibilityResponseData,
} from '@/modules/application-tracker/api';

import { InviteLeaseDetailsSegmentEvents } from '../constants';
import {
  recordInviteEligibilityBannerShown,
  recordInviteEligibilityCheckCompleted,
  recordInviteEligibilityCheckUnavailable,
  recordInviteEligibilityRecordLinkClicked,
} from '../invite-eligibility.rum';
import {
  inviteEligibilityLatencyBucket,
  inviteEligibilityRowProperties,
  setInviteEligibilityPreflightOutcome,
  summarizeInviteEligibilityCheck,
  type InviteEligibilityFlow,
  type InviteEligibilityPreflightOutcome,
} from '../utils/invite-eligibility-telemetry.util';
import type { InviteEligibilityPreflightState } from './use-invite-eligibility-check';
import { useInviteTracking } from './use-invite-tracking';

export type InviteEligibilityTrackedEvent =
  'CheckCompleted' | 'CheckUnavailable' | 'WarningShown' | 'BlockShown' | 'RecordLinkClicked';

/** Sends one eligibility event under the flow's own Segment name and shared properties. */
export type InviteEligibilityTracker = {
  flow: InviteEligibilityFlow;
  track: (event: InviteEligibilityTrackedEvent, properties: Record<string, unknown>) => void;
};

const CREATE_INVITE_ELIGIBILITY_EVENTS: Record<InviteEligibilityTrackedEvent, string> = {
  CheckCompleted: InviteLeaseDetailsSegmentEvents.EligibilityCheckCompleted,
  CheckUnavailable: InviteLeaseDetailsSegmentEvents.EligibilityCheckUnavailable,
  WarningShown: InviteLeaseDetailsSegmentEvents.EligibilityWarningShown,
  BlockShown: InviteLeaseDetailsSegmentEvents.EligibilityBlockShown,
  RecordLinkClicked: InviteLeaseDetailsSegmentEvents.ExistingRecordLinkClicked,
};

const InviteEligibilityTrackerContext = createContext<InviteEligibilityTracker | null>(null);

/** Edit invite wraps its form in this so eligibility events use the drawer's naming. */
export const InviteEligibilityTrackerProvider = InviteEligibilityTrackerContext.Provider;

const httpStatusOf = (error: unknown): number | undefined =>
  isAxiosError(error) ? error.response?.status : undefined;

const useInviteEligibilityTracker = (): InviteEligibilityTracker => {
  const provided = useContext(InviteEligibilityTrackerContext);
  const { trackInvite } = useInviteTracking();
  const createTracker = useMemo<InviteEligibilityTracker>(
    () => ({
      flow: 'create',
      track: (event, properties) =>
        trackInvite(CREATE_INVITE_ELIGIBILITY_EVENTS[event], properties),
    }),
    [trackInvite],
  );
  return provided ?? createTracker;
};

/**
 * Segment + RUM for the eligibility check. Properties carry counts, outcomes and reasons only,
 * never renter emails, record ids, addresses or units.
 */
export function useInviteEligibilityTracking() {
  const { flow, track } = useInviteEligibilityTracker();

  const trackCheckCompleted = useCallback(
    (data: InviteEligibilityResponseData, elapsedMs: number) => {
      const summary = summarizeInviteEligibilityCheck(data);
      track('CheckCompleted', {
        ...summary,
        latency_bucket: inviteEligibilityLatencyBucket(elapsedMs),
      });
      recordInviteEligibilityCheckCompleted(flow, summary, elapsedMs);
    },
    [track, flow],
  );

  const trackCheckUnavailable = useCallback(
    (elapsedMs: number, httpStatus?: number) => {
      track('CheckUnavailable', {});
      recordInviteEligibilityCheckUnavailable(flow, elapsedMs, httpStatus);
    },
    [track, flow],
  );

  const trackBannerShown = useCallback(
    (applicant: InviteEligibilityApplicant, applicantIndex: number) => {
      if (applicant.outcome === 'allow') {
        return;
      }
      const row = inviteEligibilityRowProperties(applicant, applicantIndex);
      track(applicant.outcome === 'warning' ? 'WarningShown' : 'BlockShown', row);
      recordInviteEligibilityBannerShown(flow, applicant.outcome, row);
    },
    [track, flow],
  );

  const trackRecordLinkClicked = useCallback(
    (applicant: InviteEligibilityApplicant) => {
      track('RecordLinkClicked', {
        outcome: applicant.outcome,
        match_count: applicant.total_matches,
      });
      recordInviteEligibilityRecordLinkClicked(flow, applicant.outcome, applicant.total_matches);
    },
    [track, flow],
  );

  return useMemo(
    () => ({
      flow,
      trackCheckCompleted,
      trackCheckUnavailable,
      trackBannerShown,
      trackRecordLinkClicked,
    }),
    [flow, trackCheckCompleted, trackCheckUnavailable, trackBannerShown, trackRecordLinkClicked],
  );
}

type UseTrackInviteEligibilityCheckArgs = {
  preflight: InviteEligibilityPreflightState;
  /** What the form shows right now; Submit reads it when Send is clicked. */
  shownOutcome: InviteEligibilityPreflightOutcome;
};

/**
 * Mount once per form (the eligibility controller). Each network fetch gives one Check
 * Completed or one Check Unavailable event.
 */
export function useTrackInviteEligibilityCheck({
  preflight,
  shownOutcome,
}: UseTrackInviteEligibilityCheckArgs) {
  const { flow, trackCheckCompleted, trackCheckUnavailable } = useInviteEligibilityTracking();
  const { fetchStatus, status, data, error } = preflight;
  const fetchStartedAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (fetchStatus === 'fetching') {
      fetchStartedAtRef.current ??= performance.now();
      return;
    }

    const startedAt = fetchStartedAtRef.current;
    if (startedAt === null) {
      return;
    }
    fetchStartedAtRef.current = null;
    const elapsedMs = performance.now() - startedAt;

    if (status === 'error') {
      trackCheckUnavailable(elapsedMs, httpStatusOf(error));
    } else if (status === 'success' && data) {
      trackCheckCompleted(data, elapsedMs);
    }
  }, [fetchStatus, status, data, error, trackCheckCompleted, trackCheckUnavailable]);

  useEffect(() => {
    setInviteEligibilityPreflightOutcome(flow, shownOutcome);
  }, [flow, shownOutcome]);

  useEffect(() => () => setInviteEligibilityPreflightOutcome(flow, 'not_checked'), [flow]);
}
